import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Plus, Trash2, ArchiveRestore, Archive, Search, FolderKanban, Clock, ClipboardList, MessagesSquare, Check, X, UserCheck, Eye, CalendarPlus, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { typeLabel } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";

const statusLabels = {
    planning: "Planning",
    fieldwork: "Fieldwork",
    awaiting_review: "Awaiting review",
    review_points: "Review queries to address",
    partner_review: "Partner review",
    approved: "Approved — awaiting final",
    awaiting_client_approval: "Awaiting client approval",
    finalised: "Finalised",
};
const statusStyles = {
    planning: "bg-blue-50 text-blue-800",
    fieldwork: "bg-amber-50 text-amber-800",
    awaiting_review: "bg-amber-50 text-amber-800",
    review_points: "bg-rose-50 text-rose-800",
    partner_review: "bg-indigo-50 text-indigo-800",
    approved: "bg-teal-50 text-teal-800",
    awaiting_client_approval: "bg-violet-50 text-violet-800",
    finalised: "bg-emerald-50 text-emerald-800",
};

export default function EngagementList({ archived = false }) {
    const { user } = useAuth();
    const isPartner = user?.role === "Partner";
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const status = params.get("status") || "all";

    const [q, setQ] = useState("");
    const [fAgent, setFAgent] = useState("all");
    const [fType, setFType] = useState("all");
    const [fTeam, setFTeam] = useState("all");
    const [mine, setMine] = useState(() => localStorage.getItem("qube_only_mine") === "1");
    useEffect(() => { localStorage.setItem("qube_only_mine", mine ? "1" : "0"); }, [mine]);

    const load = async () => {
        try {
            if (archived) {
                // Finalised & Archived view = manually-archived files + finalised files not yet archived.
                const [arc, act] = await Promise.all([api.get("/engagements", { params: { archived: true } }), api.get("/engagements")]);
                const map = {};
                [...arc.data, ...act.data.filter((e) => e.status === "finalised")].forEach((e) => { map[e.id] = e; });
                setItems(Object.values(map));
            } else {
                const r = await api.get("/engagements");
                setItems(r.data.filter((e) => e.status !== "finalised"));  // finalised live in the Finalised & Archived view
            }
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); }, [archived]);
    useEffect(() => { api.get("/users?role=staff").then((r) => setUsers(r.data)).catch(() => {}); }, []);
    const nameOf = (id) => users.find((u) => u.id === id)?.name;

    const agents = useMemo(() => [...new Set(items.map((e) => e.managing_agent).filter(Boolean))].sort(), [items]);
    const types = useMemo(() => [...new Set(items.map((e) => e.engagement_type).filter(Boolean))], [items]);
    const teamIdsOf = (e) => [e.preparer_id, e.auditor_id, e.manager_id, e.reviewer_id, e.partner_id].filter(Boolean);

    const statusMatch = (e) => {
        if (status === "active") return ["planning", "fieldwork"].includes(e.status);
        if (status === "review") return ["awaiting_review", "review_points", "partner_review"].includes(e.status);
        if (status === "finalised") return e.status === "finalised";
        if (statusLabels[status]) return e.status === status;  // exact single-stage filter (from Dashboard pipeline)
        return true;
    };
    const matches = (e) => {
        if (!archived && !statusMatch(e)) return false;
        if (q.trim()) {
            const hay = [e.client_name, e.property_name, e.reference, e.address, e.managing_agent].join(" ").toLowerCase();
            if (!hay.includes(q.trim().toLowerCase())) return false;
        }
        if (fAgent !== "all" && (e.managing_agent || "") !== fAgent) return false;
        if (fType !== "all" && e.engagement_type !== fType) return false;
        if (fTeam !== "all" && !teamIdsOf(e).includes(fTeam)) return false;
        if (mine && !(user?.id && teamIdsOf(e).includes(user.id))) return false;
        return true;
    };
    const shown = items.filter(matches);

    const [sort, setSort] = useState({ key: "client_name", dir: 1 });
    const sortVal = (e, key) => {
        if (key === "type") return (typeLabel(e.engagement_type) || "").toLowerCase();
        if (key === "agent") return (e.managing_agent || "").toLowerCase();
        if (key === "team") return (nameOf(e.preparer_id) || "").toLowerCase();
        if (key === "period") return e.current_period_end || "";
        if (key === "status") return e.status || "";
        return (e.client_name || "").toLowerCase();
    };
    const sorted = [...shown].sort((a, b) => { const x = sortVal(a, sort.key), y = sortVal(b, sort.key); return x < y ? -sort.dir : x > y ? sort.dir : 0; });
    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));
    const arrow = (key) => (sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : "");

    const activeFilters = (q.trim() ? 1 : 0) + (fAgent !== "all" ? 1 : 0) + (fType !== "all" ? 1 : 0) + (fTeam !== "all" ? 1 : 0) + (mine ? 1 : 0) + (!archived && status !== "all" ? 1 : 0);
    const clearAll = () => { setQ(""); setFAgent("all"); setFType("all"); setFTeam("all"); setMine(false); setParams({}); };

    const stats = {
        total: items.length,
        active: items.filter((e) => ["planning", "fieldwork"].includes(e.status)).length,
        review: items.filter((e) => ["awaiting_review", "review_points", "partner_review"].includes(e.status)).length,
    };
    const setStatus = (k) => (k === "all" ? setParams({}) : setParams({ status: k }));

    const del = async (id) => {
        if (!window.confirm("Delete this engagement and all working papers?")) return;
        try { await api.delete(`/engagements/${id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error("Delete failed"); }
    };
    const unarchive = async (id) => {
        try { await api.post(`/engagements/${id}/unarchive`); toast.success("Restored to active engagements"); load(); }
        catch (e) { toast.error("Restore failed"); }
    };
    const archiveFile = async (id) => {
        try { await api.post(`/engagements/${id}/archive`); toast.success("Archived"); load(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Archive failed"); }
    };
    const rollForward = async (e) => {
        if (!window.confirm(`Create next year's file for "${e.client_name}"?\n\nA3 (Summary of Lease) work carries over, and the trial balance rolls to the prior year.`)) return;
        try { const { data } = await api.post(`/engagements/${e.id}/roll-forward`, {}); toast.success("Rolled forward — next year's file created"); nav(`/engagements/${data.id}`); }
        catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Roll forward failed"); }
    };
    const unfinalise = async (e) => {
        if (!window.confirm("Un-finalise this file and send it back to In progress?")) return;
        try {
            if (e.archived) await api.post(`/engagements/${e.id}/unarchive`);
            await api.post(`/engagements/${e.id}/transition`, { action: "reopen" });
            toast.success("File re-opened — back in your active engagements"); load();
        } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Could not un-finalise"); }
    };

    const selCls = "text-xs border border-border rounded-sm px-2 py-1.5 bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-brand/30";

    return (
        <div className="p-8 max-w-7xl" data-testid={archived ? "archived-list-page" : "engagement-list-page"}>
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">{archived ? "Completed & put away" : "Firm overview"}</div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">{archived ? "Finalised & Archived" : "Engagement overview"}</h1>
                </div>
                <div className="flex items-center gap-2">
                    {!archived && (
                        <Link to="/archived" data-testid="goto-archived-btn" className="flex items-center gap-1.5 border border-border hover:border-brand text-sm font-medium px-4 py-2 rounded-sm">
                            <Archive className="w-4 h-4" /> Finalised &amp; archived
                        </Link>
                    )}
                    {archived && (
                        <Link to="/" data-testid="back-to-overview-btn" className="flex items-center gap-1.5 border border-border hover:border-brand text-sm font-medium px-4 py-2 rounded-sm">
                            <FolderKanban className="w-4 h-4" /> Active engagements
                        </Link>
                    )}
                    {!archived && (
                        <Link to="/engagements/new" data-testid="new-engagement-btn" className="flex items-center gap-1.5 bg-brand hover:bg-brand-hover text-white text-sm font-medium px-4 py-2 rounded-sm">
                            <Plus className="w-4 h-4" /> New engagement
                        </Link>
                    )}
                </div>
            </div>

            {!archived && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                    <StatCard testId="stat-total" label="Total engagements" value={stats.total} icon={FolderKanban} active={status === "all"} onClick={() => setStatus("all")} />
                    <StatCard testId="stat-active" label="In progress" value={stats.active} icon={Clock} accent="amber" active={status === "active"} onClick={() => setStatus("active")} />
                    <StatCard testId="stat-review" label="Awaiting review" value={stats.review} icon={ClipboardList} accent="indigo" active={status === "review"} onClick={() => setStatus("review")} />
                </div>
            )}

            {!archived && <ClientQueriesPanel engagements={items} />}

            {/* Search + filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="engagement-toolbar">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input data-testid="engagement-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, property, reference, address…"
                        className="w-full text-sm border border-border rounded-sm pl-8 pr-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand/30" />
                </div>
                <select data-testid="filter-agent" value={fAgent} onChange={(e) => setFAgent(e.target.value)} className={selCls} title="Managing agent">
                    <option value="all">All managing agents</option>
                    {agents.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <select data-testid="filter-type" value={fType} onChange={(e) => setFType(e.target.value)} className={selCls} title="Job type">
                    <option value="all">All job types</option>
                    {types.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                </select>
                <select data-testid="filter-team" value={fTeam} onChange={(e) => setFTeam(e.target.value)} className={selCls} title="Team member">
                    <option value="all">All team members</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
                {!archived && (
                    <select data-testid="filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selCls} title="Status">
                        <option value="all">All statuses</option>
                        <option value="active">In progress</option>
                        <option value="review">Awaiting review</option>
                    </select>
                )}
                <button data-testid="filter-mine" onClick={() => setMine((v) => !v)} aria-pressed={mine} title="Show only engagements where you are on the team"
                    className={`text-xs font-medium px-3 py-1.5 rounded-sm border inline-flex items-center gap-1.5 ${mine ? "bg-brand text-white border-brand" : "border-border text-muted-foreground hover:bg-slate-50"}`}>
                    <UserCheck className="w-3.5 h-3.5" /> Only my engagements
                </button>
                {activeFilters > 0 && (
                    <button data-testid="clear-filters" onClick={clearAll} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1 px-2 py-1.5">
                        <X className="w-3.5 h-3.5" /> Clear
                    </button>
                )}
                <span className="text-xs text-muted-foreground ml-auto font-num" data-testid="engagement-count">{sorted.length} of {items.length}</span>
            </div>

            <div className="bg-white border border-border rounded-sm">
                <table className="tbl">
                    <thead>
                        <tr>
                            <th><button data-testid="sort-client_name" onClick={() => toggleSort("client_name")} className="hover:text-brand font-inherit">Client{arrow("client_name")}</button></th>
                            <th><button data-testid="sort-agent" onClick={() => toggleSort("agent")} className="hover:text-brand font-inherit">Managing agent{arrow("agent")}</button></th>
                            <th><button data-testid="sort-type" onClick={() => toggleSort("type")} className="hover:text-brand font-inherit">Type{arrow("type")}</button></th>
                            <th><button data-testid="sort-team" onClick={() => toggleSort("team")} className="hover:text-brand font-inherit">Team{arrow("team")}</button></th>
                            <th><button data-testid="sort-period" onClick={() => toggleSort("period")} className="hover:text-brand font-inherit">Current period{arrow("period")}</button></th>
                            <th><button data-testid="sort-status" onClick={() => toggleSort("status")} className="hover:text-brand font-inherit">Status{arrow("status")}</button></th>
                            <th>Client queries</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody data-testid="engagement-rows">
                        {sorted.map((e) => (
                            <tr key={e.id}>
                                <td className="font-medium cursor-pointer" onClick={() => nav(`/engagements/${e.id}`)}>{e.client_name}</td>
                                <td className="text-muted-foreground">{e.managing_agent || "—"}</td>
                                <td><span className="text-xs px-2 py-0.5 bg-slate-100 rounded-sm font-medium">{typeLabel(e.engagement_type)}</span></td>
                                <td className="text-xs" data-testid={`engagement-team-${e.id}`}>
                                    <div><span className="text-[9px] uppercase tracking-wider text-muted-foreground font-display">Preparer </span>{nameOf(e.preparer_id) || "—"}</div>
                                    {e.engagement_type === "prep_audit" && <div><span className="text-[9px] uppercase tracking-wider text-muted-foreground font-display">Auditor </span>{nameOf(e.auditor_id) || "—"}</div>}
                                    {e.engagement_type === "prep_audit" && e.awaiting_auditor_count > 0 && (
                                        <div className="mt-0.5"><span data-testid={`awaiting-auditor-${e.id}`} title="Prep papers still awaiting the auditor's review" className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800"><Eye className="w-3 h-3" /> {e.awaiting_auditor_count} awaiting auditor</span></div>
                                    )}
                                </td>
                                <td className="font-num text-xs">{e.current_period_start?.slice(0, 10)} → {e.current_period_end?.slice(0, 10)}</td>
                                <td><span className={`text-[11px] px-2 py-0.5 rounded-sm font-semibold uppercase tracking-wider ${statusStyles[e.status]}`}>{statusLabels[e.status]}</span></td>
                                <td>
                                    {e.client_all_answered
                                        ? <span data-testid={`client-status-${e.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800">Client replies in</span>
                                        : e.client_query_open
                                            ? <span data-testid={`client-status-${e.id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800">Awaiting client</span>
                                            : <span className="text-muted-foreground text-xs">—</span>}
                                </td>
                                <td className="text-right">
                                    {archived
                                        ? (
                                            <div className="flex items-center justify-end gap-2">
                                                {can(user, "roll_forward") && <button data-testid={`roll-forward-${e.id}`} onClick={() => rollForward(e)} title="Create next year's file (A3 carries over, TB rolls to prior year)" className="text-muted-foreground hover:text-brand inline-flex items-center gap-1 text-xs"><CalendarPlus className="w-4 h-4" /> Roll forward</button>}
                                                {e.status === "finalised" && <button data-testid={`unfinalise-${e.id}`} onClick={() => unfinalise(e)} title="Send back to In progress" className="text-muted-foreground hover:text-amber-700 inline-flex items-center gap-1 text-xs"><Undo2 className="w-4 h-4" /> Un-finalise</button>}
                                                {e.archived
                                                    ? (can(user, "unarchive_engagement") && <button data-testid={`unarchive-engagement-${e.id}`} onClick={() => unarchive(e.id)} title="Restore to active" className="text-muted-foreground hover:text-brand inline-flex items-center gap-1 text-xs"><ArchiveRestore className="w-4 h-4" /> Restore</button>)
                                                    : (can(user, "archive_engagement") && <button data-testid={`archive-engagement-${e.id}`} onClick={() => archiveFile(e.id)} title="Archive (put away)" className="text-muted-foreground hover:text-brand inline-flex items-center gap-1 text-xs"><Archive className="w-4 h-4" /> Archive</button>)}
                                            </div>
                                        )
                                        : (can(user, "delete_engagement")
                                            ? <button data-testid={`delete-engagement-${e.id}`} onClick={() => del(e.id)} title="Delete engagement (Partner only)" className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                                            : <span className="text-muted-foreground text-xs">—</span>)}
                                </td>
                            </tr>
                        ))}
                        {sorted.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-muted-foreground py-10">
                                {archived ? "No finalised or archived files yet."
                                    : activeFilters > 0 ? <>No engagements match your search / filters. <button onClick={clearAll} className="text-brand underline">Clear filters</button></>
                                        : <>No engagements yet. <Link to="/engagements/new" className="text-brand underline">Create the first one →</Link></>}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, accent = "slate", testId, active, onClick }) {
    const accents = { slate: "text-brand-slate", amber: "text-amber-700", indigo: "text-indigo-700", emerald: "text-emerald-700" };
    return (
        <button data-testid={testId} onClick={onClick}
            className={`text-left bg-white border rounded-sm p-4 block transition-all ${active ? "border-brand shadow-sm ring-1 ring-brand/30" : "border-border hover:border-brand hover:shadow-sm"}`}>
            <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-display">{label}</div>
                <Icon className={`w-4 h-4 ${accents[accent]}`} />
            </div>
            <div className="font-display font-bold text-3xl mt-2 tracking-tight">{value}</div>
        </button>
    );
}

function ClientQueriesPanel({ engagements }) {
    const waiting = engagements
        .filter((e) => e.client_query_open)
        .sort((a, b) => (a.client_all_answered === b.client_all_answered ? 0 : a.client_all_answered ? 1 : -1));
    if (waiting.length === 0) return null;
    return (
        <div className="bg-white border border-border rounded-sm mb-6" data-testid="dashboard-client-queries">
            <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                <MessagesSquare className="w-4 h-4 text-brand" />
                <div className="font-display font-semibold text-sm">Files waiting on clients</div>
                <span className="text-[11px] text-muted-foreground">{waiting.length} file{waiting.length === 1 ? "" : "s"} with open client queries</span>
            </div>
            <div className="divide-y divide-border">
                {waiting.map((e) => (
                    <Link key={e.id} to={`/engagements/${e.id}`} data-testid={`dashboard-client-row-${e.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{e.client_name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{e.managing_agent || "No managing agent"}</div>
                        </div>
                        {e.client_all_answered
                            ? <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800"><Check className="w-3.5 h-3.5" /> All client replies in</span>
                            : <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-sm bg-amber-100 text-amber-800"><MessagesSquare className="w-3.5 h-3.5" /> Awaiting client</span>}
                    </Link>
                ))}
            </div>
        </div>
    );
}
